import React, { useState, useEffect } from 'react';
import { useClient } from '../../context/ClientContext';

const deviceIcons = {
  Mobile: (
    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5}
        d="M10.5 1.5H8.25A2.25 2.25 0 006 3.75v16.5a2.25 2.25 0 002.25 2.25h7.5A2.25 2.25 0 0018 20.25V3.75a2.25 2.25 0 00-2.25-2.25H13.5m-3 0V3h3V1.5m-3 0h3m-3 18.75h3" />
    </svg>
  ),
  Desktop: (
    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5}
        d="M9 17.25v1.007a3 3 0 01-.879 2.122L7.5 21h9l-.621-.621A3 3 0 0115 18.257V17.25m6-12V15a2.25 2.25 0 01-2.25 2.25H5.25A2.25 2.25 0 013 15V5.25A2.25 2.25 0 015.25 3h13.5A2.25 2.25 0 0121 5.25z" />
    </svg>
  ),
  Tablet: (
    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5}
        d="M10.5 19.5h3m-6.75 2.25h10.5a2.25 2.25 0 002.25-2.25V4.5a2.25 2.25 0 00-2.25-2.25H6.75A2.25 2.25 0 004.5 4.5v15a2.25 2.25 0 002.25 2.25z" />
    </svg>
  )
};

export default function DeviceBreakdown({ dateRange }) {
  const { brand, fetcher } = useClient();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    fetcher.fetchDevicePerformance(dateRange)
      .then(setData)
      .finally(() => setLoading(false));
  }, [dateRange]);

  if (loading) {
    return (
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 animate-pulse">
        <div className="h-4 bg-gray-200 rounded w-1/3 mb-4"></div>
        <div className="space-y-3">
          {[...Array(3)].map((_, i) => <div key={i} className="h-12 bg-gray-200 rounded"></div>)}
        </div>
      </div>
    );
  }

  if (!data?.devices?.length) return null;

  const totalCost = data.devices.reduce((s, d) => s + d.cost, 0);
  const currency = data.currency || '$';
  const primaryColor = brand.colors?.primary || '#2563eb';

  return (
    <div className="bg-white rounded-xl shadow-sm hover:shadow-md transition-all duration-200 border border-gray-100 p-6">
      <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wide mb-1"
          style={{ fontFamily: brand.typography?.headerFont }}>
        Device Performance
      </h3>
      <p className="text-xs text-gray-400 mb-4">Selected period</p>

      <div className="space-y-3">
        {data.devices.map((device) => {
          const pct = totalCost > 0 ? (device.cost / totalCost) * 100 : 0;
          const icon = deviceIcons[device.device] || deviceIcons.Desktop;

          return (
            <div key={device.device} className="space-y-1.5">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <span className="text-gray-400">{icon}</span>
                  <span className="text-sm font-medium text-gray-900">{device.device}</span>
                </div>
                <span className="text-xs text-gray-500">{pct.toFixed(0)}% of spend</span>
              </div>

              <div className="w-full bg-gray-100 rounded-full h-2">
                <div
                  className="h-2 rounded-full transition-all duration-500"
                  style={{ width: `${pct}%`, backgroundColor: primaryColor }}
                />
              </div>

              <div className="flex gap-4 text-xs text-gray-500">
                <span>{device.conversions} conv.</span>
                <span>{currency}{device.cpa.toFixed(0)} CPA</span>
                <span>{device.convRate.toFixed(1)}% conv. rate</span>
                <span>{device.clicks.toLocaleString()} clicks</span>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
